/**
 * ACTIVITY-CORE (L3 ligero) — página "Actividad": el feed del hogar (quién hizo
 * qué en recetas, planner, lista de la compra y despensa). Fuente local-first:
 * bucket `activity_recent` (~90 días); el historial más antiguo se pagina bajo
 * demanda desde la API.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, Show } from "solid-js";
import { History } from "lucide-solid";
import { type ActivityRow, getActivityFeedQuery, queryKeys } from "@mealplan/shared";
import { createPowerSyncQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { ActivityList } from "./activity-list";
import { useActivityNameResolver } from "../lib/use-activity-names";
import { loadOlderActivity } from "../lib/feed-api";

const LOCAL_FEED_LIMIT = 200;
const API_PAGE_SIZE = 50;

export function ActivityFeedPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const resolveName = useActivityNameResolver({ db, drizzle, userId });

  // Historial profundo (API), acumulado bajo el feed local.
  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const localQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.feed(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getActivityFeedQuery(dbInstance, { limit: LOCAL_FEED_LIMIT }) : null;
    },
    enabled: () => Boolean(drizzle()),
  });

  const rows = createMemo<ActivityRow[]>(() => {
    const local = localQuery.data ?? [];
    if (olderRows().length === 0) {
      return local;
    }
    const seen = new Set(local.map((r) => r.id));
    return [...local, ...olderRows().filter((r) => !seen.has(r.id))];
  });

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const last = rows()[rows().length - 1];
      const page = await loadOlderActivity(last?.id ?? null, API_PAGE_SIZE);
      setOlderRows((prev) => [...prev, ...page.rows]);
      setHasMoreOlder(Boolean(page.nextBefore));
    } catch {
      showToast(t(msg`Could not load older activity`), "error");
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div class="space-y-6" data-testid="activity-page">
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <History class="h-5 w-5" />
        </div>
        <div>
          <h1 class="text-2xl font-bold tracking-tight">{t(msg`Activity`)}</h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Who did what in your household — recipes, planner, shopping and pantry.`)}
          </p>
        </div>
      </div>

      <ActivityList
        rows={rows}
        isLoading={() => localQuery.isFetching && (localQuery.data ?? []).length === 0}
        grouped
        currentUserId={userId}
        canRevert
        resolveName={resolveName}
      />

      <Show when={rows().length >= API_PAGE_SIZE && hasMoreOlder()}>
        <div class="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingOlder()}
            onClick={() => void loadOlder()}
            data-testid="activity-load-older"
          >
            {loadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
